<template>
	<BaseLayout :transparent-header="true" :footer-full="true" :include-padding="false">
		<header class="pt-400 pt-600-lg bg-color-content-primary text-color-white">
			<div class="container display-flex align-items-center align-items-stretch-lg flex-wrap-wrap">
				<div class="text-align-center-smo col-12 col-6-md pt-300 pb-100 py-400-md pr-400-lg">
					<h1 class="overline text-color-gray-20"> Private Credit </h1>
					<p v-reveal:fade-up class="heading-1 mt-200-md mt-100-smo">
						An opportunistic strategy for income-focused investors
					</p>
					<p class="delta mt-50 mt-100-smo mb-200 col-11-lg">
						Our new private credit investment strategy capitalizes on the changed economic environment,
						offering some of the most attractive potential risk-adjusted returns of the past decade.
					</p>
					<LeadCaptureComponent class="text-align-left" :form-config="presetPlanLeadCaptureConfig" />
				</div>
				<figure class="mx-0 my-0 col-12 col-6-md mt-200-smo">
					<img v-reveal:fade-up data-test="hero-image" :src="imgUrl" :alt="imgAlt" />
				</figure>
			</div>
			<BasePlaceHolder
				v-if="isPartnerPage"
				class="container pb-200 mt-200-smo"
				:placehold="loading"
				theme="dark"
				:line-count="3"
			>
				<SolicitorNotice
					data-test="solicitor-notice"
					:solicitor="solicitorName"
					:payment="solicitorPayment"
					:is-cpa-partner="solicitorIsCpaPartner"
					:is-investor="solicitorIsInvestor"
					:monthly-payment="solicitorMonthlyPayment"
					:custom-payment-condition="solicitorCustomCondition"
					:text-dark="false"
				/>
			</BasePlaceHolder>
		</header>
		<WhyPrivateCreditSection />
		<OurTrackRecordSection />
		<AboutOurStrategySection />
		<OurBankAndRealEstatePartnersSection />
		<CtaSection theme="dark">
			<template #cta>
				<LeadCaptureComponent
					class="mt-300-md mt-200-smo mr-300-md mr-600-lg"
					:form-config="presetPlanLeadCaptureConfig"
				/>
			</template>
		</CtaSection>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import AboutOurStrategySection from '@components/marketing-site/private-credit/about-our-strategy-section.vue';
import { appCloudfrontPath } from '@constants';
import CtaSection from '@components/marketing-site/cta-section.vue';
import { currency } from '@filters/shared-filters';
import { defaultSolicitorNotice } from '@constants/marketing-site';
import { getMediaPartnerNotices } from '@api/sanity/graphql/media-partner-notices';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { MediaPartnerNoticeData } from 'types/sanity/marketing-components';
import OurBankAndRealEstatePartnersSection from '@components/marketing-site/private-credit/our-banking-and-real-estate-partners-section.vue';
import OurTrackRecordSection from '@components/marketing-site/private-credit/our-track-record-section.vue';
import SolicitorNotice from '@components/marketing-site/solicitor-notice.vue';
import { supplementalIncomePlanId } from '@constants/plans-info';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import WhyPrivateCreditSection from '@components/marketing-site/private-credit/why-private-credit-section.vue';
const route = useRoute();

interface Props {
	isPartnerPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isPartnerPage: false
});

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const imgAlt = 'Fundrise portfolio snapshot';
const metaTitle = 'Private Credit';
const metaDescription =
	'Our private credit strategy capitalizes on market shifts to target the best risk-adjusted returns in the past decade.';

const presetPlanLeadCaptureConfig: LeadCaptureFormConfig = {
	buttonText: 'Get started',
	signupFlowType: 'PRESET_PLAN',
	presetPlanId: supplementalIncomePlanId,
	thirdPartyAuthTheme: 'SECONDARY'
};

const solicitorName = ref(defaultSolicitorNotice.name);
const solicitorPayment = ref(defaultSolicitorNotice.payment);
const solicitorMonthlyPayment = ref(defaultSolicitorNotice.monthlyPayment);
const solicitorIsCpaPartner = ref(defaultSolicitorNotice.isCpaPartner);
const solicitorIsInvestor = ref(defaultSolicitorNotice.isInvestor);
const solicitorCustomCondition = ref(defaultSolicitorNotice.customCondition);

const loading = ref(true);

const imgUrl = computed((): string => {
	return `${appCloudfrontPath}/images/marketing-site/campaign/fundrise-porfolio-snapshot-${
		appStore.isMobile ? 'mobile' : 'desktop'
	}.png`;
});

const utmSource = computed((): string => (route.query.utm_source ? route.query.utm_source.toString() : ''));

onBeforeMount(() => {
	if (props.isPartnerPage) {
		thirdPartyAuthStore.disable(onBeforeUnmount);
	}
});

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});

	if (props.isPartnerPage) {
		const partnerNoticesResponse = await getMediaPartnerNotices('income-preset-plan');

		const partnerNotices = partnerNoticesResponse?.partnerNotices ?? [];
		const solicitorDetails: MediaPartnerNoticeData | null =
			partnerNotices.find((p: MediaPartnerNoticeData) => p.utmSource === utmSource.value) || null;

		solicitorName.value = solicitorDetails?.name || solicitorName.value;
		solicitorPayment.value = solicitorDetails?.payment || currency(50, 0);
		solicitorMonthlyPayment.value = solicitorDetails?.monthlyPayment || solicitorMonthlyPayment.value;
		solicitorIsInvestor.value = solicitorDetails?.isInvestor || false;
		solicitorIsCpaPartner.value = solicitorDetails?.isCpaPartner || false;
	}
	loading.value = false;
}

setup();
</script>
